import { ReactNode, useEffect, useMemo, useState } from 'react';
import createSafeContext from '@paradigm/utils/src/createSafeContext';
import { useObservable } from '@paradigm/utils/src/hooks';

import UnifiedRfqsListService from '#/unified-rfqs/services/unified-rfqs';

import {
  hasConflictingProductSelection,
  useRfqFiltersValue,
} from '#/unified-rfqs/ui/rfq-filters/filter-provider';
import { RfqFilters } from '#/unified-rfqs/services/rfq-filtering';
import { useGrfqService } from '#/unified-rfqs/contexts/grfqs';
import {
  useDrfqNotificationsService,
  useDrfqService,
} from '#/unified-rfqs/contexts/drfqs';
import { useUnifiedMarketsNotifications } from '#/unified-rfqs/ui/notifications/notifications-context';
import RfqsStore from '#/unified-rfqs/store/rfqs';
import { useApi } from '@paradigm/api/src/fetch-api';
import { useInterval } from '@paradigm/utils/src/useInterval';
import { Instrument } from '../entities/drfq/instruments';

export const [useUnifiedRfqsService, BaseRfqServiceProvider] =
  createSafeContext<UnifiedRfqsListService>('RfqService');

interface Props {
  readonly children: ReactNode;
  readonly rfqsStore: RfqsStore;
}

export function UnifiedRfqsServiceProvider({ children, rfqsStore }: Props) {
  const grfqService = useGrfqService();
  const drfqService = useDrfqService();
  const { drfqFutureInstruments } = useDrfqFutureInstruments();

  const { state: filterState } = useRfqFiltersValue();
  const filters: RfqFilters = useMemo(
    () => ({
      auctionTypes: filterState.auctionTypes,
      productCodes: filterState.productCodes,
      strategyCodes: filterState.strategyCodes,
      notional: filterState.notional,
      counterparties: filterState.counterparties.map((s) => s.value),
      hasConflictingProductSelection:
        hasConflictingProductSelection(filterState),
    }),
    [filterState],
  );
  const rfqService = useMemo(
    () =>
      new UnifiedRfqsListService({
        drfqService,
        grfqService,
        rfqsStore,
        filters,
      }),
    [drfqService, grfqService, rfqsStore, filters],
  );
  useEffect(() => {
    rfqService.setDrfqFutureInstruments(drfqFutureInstruments);
  }, [drfqFutureInstruments, rfqService]);

  // Keep the notifications the user gets in step with the list they see: an
  // RFQ the filter hides never reaches the table, so it must not pop a toast
  // either. The app-wide service takes the same filter for as long as this
  // one is mounted, so navigating away does not bring them back; dropping it
  // on the way out hands the say back to the stored filter.
  const notificationsService = useDrfqNotificationsService();
  const { setRfqNotificationFilter } = useUnifiedMarketsNotifications();
  useEffect(() => {
    const rfqFilter = { filters, drfqFutureInstruments };
    notificationsService.setRfqFilter(rfqFilter);
    setRfqNotificationFilter(rfqFilter);

    return () => setRfqNotificationFilter(null);
  }, [
    notificationsService,
    setRfqNotificationFilter,
    filters,
    drfqFutureInstruments,
  ]);

  return (
    <BaseRfqServiceProvider key={rfqService.id} value={rfqService}>
      {children}
    </BaseRfqServiceProvider>
  );
}

export function useRfqsFetchData() {
  const { fetcher } = useUnifiedRfqsService();
  return useObservable(fetcher.fetchData);
}

export function useRfqIdsList() {
  const { fetcher } = useUnifiedRfqsService();
  const [rfqIds, setRfqIds] = useState(fetcher.selectPaginatedIds());
  useEffect(() => {
    const unsub = fetcher.subscribePaginatedIds(setRfqIds);
    return () => {
      unsub();
    };
  }, [fetcher]);
  return rfqIds;
}

function useDrfqFutureInstruments() {
  const drfqService = useDrfqService();
  const [, fetchDrfqInstruments] = useApi(drfqService.listInstruments);
  const [drfqFutureInstruments, setDrfqFutureInstruments] = useState<
    readonly Instrument[]
  >([]);

  useInterval(() => {
    if (process.env.NODE_ENV === 'test') return;
    fetchDrfqInstruments({ kind: 'FUTURE', includeGreeks: true }, (resp) => {
      if (resp.ok) {
        setDrfqFutureInstruments(resp.data.results);
      }
    });
  }, 15_000);

  return { drfqFutureInstruments };
}
